export const usePlaybackStore = defineStore('playback', () => {
  // Identity, not display: sequence *names* are not unique — duplicating a
  // sequence on the hardware without renaming it produces multiple
  // directory entries sharing a name but with distinct offsets (see
  // FORMAT.md). `blobOffset` (attached by the .ALL parser) is always
  // unique, so it — not `.name` — is what determines "is this the
  // sequence that's playing/queued."
  const activeSequenceKey = ref<number | null>(null)
  const activeController = shallowRef<any>(null)
  // Set when the user picks a *different* sequence while one is already
  // playing — real MPC behavior is to keep the current loop running and
  // switch at its next loop boundary rather than cutting it off mid-bar.
  // Cleared once the switch happens (or if the queued pick is canceled).
  const queuedSequence = shallowRef<{ sequence: any; programsByName: Map<string, any> } | null>(null)
  // The sequence currently shown in the Trk Mixer / context screen —
  // follows whatever's playing, but stays put after stop so you can keep
  // inspecting it.
  const focusedSequence = shallowRef<any>(null)
  // null = context screen shows "Now Playing"; a track number selects
  // that track's MIDI grid / audio waveform instead.
  const selectedTrackNumber = ref<number | null>(null)
  // A new object reference every time a note fires, so components can
  // `watch` it to flash the right pad — see PadGrid.vue.
  const lastFlash = shallowRef<{
    programName: string
    padIndex: number
    trackNumber?: number
    note: number
    velocity: number
    noteFallback: any
    seq: number
  } | null>(null)
  let flashSeq = 0

  // Current bar (1-indexed) within the focused/playing sequence's loop,
  // updated continuously while playing — drives the Now Playing playhead.
  const playheadBar = ref(1)
  let rafHandle: number | null = null
  let queuedSwitchTimer: ReturnType<typeof setTimeout> | null = null
  const activePlaybackItem = shallowRef<any>(null)

  function playbackKey(item: any) {
    return item?.blobOffset ?? null
  }

  function isSong(item: any) {
    return item?.kind === 'song' && Array.isArray(item.steps)
  }

  function expandedSongEntries(song: any) {
    const entries: { sequence: any; step: any; repeat: number }[] = []
    for (const step of song.steps ?? []) {
      if (!step.sequence) continue
      const repeats = Math.max(1, step.repeats || 1)
      for (let repeat = 0; repeat < repeats; repeat++) {
        entries.push({ sequence: step.sequence, step, repeat })
      }
    }
    return entries
  }

  function tickPlayhead() {
    const controller = activeController.value
    const seq = focusedSequence.value
    if (!controller || !seq) return
    const engine = useAudioEngine()
    const elapsed = (engine.ctx.currentTime - controller.startedAt) % controller.loopLengthSeconds
    const secondsPerBar = controller.loopLengthSeconds / seq.bars
    playheadBar.value = Math.floor(elapsed / secondsPerBar) + 1
    rafHandle = requestAnimationFrame(tickPlayhead)
  }

  /**
   * Start playing `sequence` right now, replacing whatever's currently
   * playing. `startAt`, if given, is an exact AudioContext time to start
   * on (used for a queued switch, so the new sequence begins sample-
   * accurately at the old one's loop boundary instead of re-deriving a
   * fresh lead-in — see `queueSwitch`).
   */
  function play(sequence: any, programsByName: Map<string, any>, startAt?: number, stopOldAt?: number) {
    if (isSong(sequence)) {
      playSong(sequence, programsByName, startAt, stopOldAt)
      return
    }
    const engine = useAudioEngine()
    stop(stopOldAt)

    const overrides = useOverridesStore().resolverFor(sequence.blobOffset)
    activeController.value = engine.playSequence(sequence, programsByName, {
      loop: true,
      overrides,
      startAt,
      onEvent: (resolvedEvent: any) => {
        lastFlash.value = {
          programName: resolvedEvent.track.programName,
          padIndex: resolvedEvent.pad.index,
          trackNumber: resolvedEvent.track.number,
          note: resolvedEvent.note,
          velocity: resolvedEvent.velocity,
          noteFallback: resolvedEvent.noteFallback,
          seq: flashSeq++,
        }
      },
    })
    activeSequenceKey.value = playbackKey(sequence)
    activePlaybackItem.value = sequence
    focusedSequence.value = sequence
    playheadBar.value = 1
    if (rafHandle) cancelAnimationFrame(rafHandle)
    rafHandle = requestAnimationFrame(tickPlayhead)
  }

  function playSong(song: any, programsByName: Map<string, any>, startAt?: number, stopOldAt?: number, startEntryIndex = 0) {
    const entries = expandedSongEntries(song)
    if (!entries.length) return

    const engine = useAudioEngine()
    stop(stopOldAt)

    // Start scheduling the next song step well before the exact sequence
    // boundary. A dense first beat can miss a 60ms JS timer window, at which
    // point Web Audio has to clamp late notes to "now" and the handoff feels
    // like the tempo is falling apart.
    const leadIn = 0.24
    let stopped = false
    let currentController: any = null
    let currentSequence: any = null
    let currentEntryIndex = Math.max(0, Math.min(entries.length - 1, startEntryIndex))
    let stepTimer: ReturnType<typeof setTimeout> | null = null
    // Song steps can carry long one-shots across sequence boundaries. Keep
    // old step controllers alive so their already-triggered sources finish
    // naturally, then stop the whole set when song mode itself stops/ends.
    const songControllers = new Set<any>()
    const wrapper: any = {
      get resolvedEvents() {
        return currentController?.resolvedEvents ?? []
      },
      get audioLayers() {
        return currentController?.audioLayers ?? []
      },
      get loopLengthSeconds() {
        return currentController?.loopLengthSeconds ?? 0
      },
      get startedAt() {
        return currentController?.startedAt ?? startAt ?? engine.ctx.currentTime
      },
      get trackBuses() {
        return currentController?.trackBuses ?? new Map()
      },
      get currentSequenceKey() {
        return currentSequence?.blobOffset ?? null
      },
      get currentSongEntryIndex() {
        return currentEntryIndex
      },
      setVarispeed(value: number) {
        currentController?.setVarispeed?.(value)
      },
      stop(at?: number) {
        stopped = true
        if (stepTimer) clearTimeout(stepTimer)
        stepTimer = null
        for (const controller of songControllers) controller.stop(at)
        songControllers.clear()
      },
    }

    activeController.value = wrapper
    activeSequenceKey.value = playbackKey(song)
    activePlaybackItem.value = song
    playheadBar.value = 1
    if (rafHandle) cancelAnimationFrame(rafHandle)
    rafHandle = requestAnimationFrame(tickPlayhead)

    const scheduleEntry = (entryIndex: number, entryStartAt?: number) => {
      if (stopped) return
      if (entryIndex >= entries.length) {
        stop(entryStartAt)
        return
      }
      currentEntryIndex = entryIndex
      const entry = entries[entryIndex]!
      const sequence = entry.sequence
      currentSequence = sequence
      focusedSequence.value = sequence

      const overrides = useOverridesStore().resolverFor(sequence.blobOffset)
      const controller = engine.playSequence(sequence, programsByName, {
        loop: false,
        overrides,
        startAt: entryStartAt,
        onEvent: (resolvedEvent: any) => {
          lastFlash.value = {
            programName: resolvedEvent.track.programName,
            padIndex: resolvedEvent.pad.index,
            trackNumber: resolvedEvent.track.number,
            note: resolvedEvent.note,
            velocity: resolvedEvent.velocity,
            noteFallback: resolvedEvent.noteFallback,
            seq: flashSeq++,
          }
        },
      })
      currentController = controller
      songControllers.add(controller)

      const nextStartAt = controller.startedAt + controller.loopLengthSeconds
      const nextEntryIndex = entryIndex + 1
      const isFinalEntry = nextEntryIndex >= entries.length
      const delayMs = Math.max(0, (nextStartAt - engine.ctx.currentTime - (isFinalEntry ? 0 : leadIn)) * 1000)
      stepTimer = setTimeout(() => {
        stepTimer = null
        if (stopped) return
        scheduleEntry(nextEntryIndex, nextStartAt)
      }, delayMs)
    }

    scheduleEntry(currentEntryIndex, startAt)
  }

  /**
   * Cleanly restart whatever's currently playing, from bar 1, picking up
   * the live overrides store's current state fresh (varispeed above all)
   * — used to settle a varispeed knob change once it stops moving: live
   * voice retuning gives instant feedback *during* the drag, but only a
   * genuine stop+restart guarantees no scheduling/choke-group state from
   * the old rate lingers. No-op if nothing's playing, or if a different
   * sequence is already queued to take over — restarting would silently
   * cancel that queue, clobbering an unrelated, already-expressed intent.
   */
  function restartIfPlaying(programsByName: Map<string, any>) {
    if (activeSequenceKey.value === null || !activePlaybackItem.value) return
    if (queuedSequence.value) return
    if (isSong(activePlaybackItem.value)) {
      const currentEntryIndex = activeController.value?.currentSongEntryIndex ?? 0
      playSong(activePlaybackItem.value, programsByName, undefined, undefined, currentEntryIndex)
      return
    }
    play(activePlaybackItem.value, programsByName)
  }

  /**
   * Queue `sequence` to take over from whatever's currently playing, right
   * at its current loop's boundary — computed once, up front, from the
   * playing controller's own clock (the same math tickPlayhead uses), not
   * re-derived later from an early "about to loop" callback (which used to
   * leave a ~50-100ms gap: old audio cut on the spot, new audio waiting on
   * its own fresh lead-in). The new sequence is given that exact boundary
   * time as its `startAt`, so the handoff is sample-accurate.
   */
  function queueSwitch(sequence: any, programsByName: Map<string, any>) {
    const ctrl = activeController.value
    if (!ctrl) {
      play(sequence, programsByName)
      return
    }
    const engine = useAudioEngine()
    const elapsed = (engine.ctx.currentTime - ctrl.startedAt) % ctrl.loopLengthSeconds
    const remaining = ctrl.loopLengthSeconds - elapsed
    const targetTime = engine.ctx.currentTime + remaining

    queuedSequence.value = { sequence, programsByName }
    if (queuedSwitchTimer) clearTimeout(queuedSwitchTimer)
    const leadIn = 0.06 // same pre-scheduling headroom playSequence itself uses
    const delayMs = Math.max(0, (remaining - leadIn) * 1000)
    queuedSwitchTimer = setTimeout(() => {
      queuedSwitchTimer = null
      const next = queuedSequence.value
      if (!next) return // canceled before it fired
      queuedSequence.value = null
      // Same instant for both ends of the handoff: the outgoing sequence's
      // last sources stop exactly when the incoming one's first sources
      // start, both scheduled directly on the AudioContext clock rather
      // than "whenever this setTimeout callback happens to run" — see
      // audio-engine.js's controller.stop for why that used to leave a
      // few-ms gap or overlap.
      play(next.sequence, next.programsByName, targetTime, targetTime)
    }, delayMs)
  }

  function cancelQueuedSwitch() {
    queuedSequence.value = null
    if (queuedSwitchTimer) {
      clearTimeout(queuedSwitchTimer)
      queuedSwitchTimer = null
    }
  }

  /** Click handler for a sequence row's Play/Stop/Queued button. */
  function toggle(sequence: any, programsByName: Map<string, any>) {
    if (queuedSequence.value?.sequence === sequence) {
      // Clicking the already-queued sequence again cancels the queue.
      cancelQueuedSwitch()
      return
    }
    if (activeSequenceKey.value === playbackKey(sequence)) {
      cancelQueuedSwitch()
      stop()
      return
    }
    if (activeSequenceKey.value === null) {
      play(sequence, programsByName)
      return
    }
    // Something else is already playing — queue for its loop boundary
    // instead of cutting it off immediately.
    queueSwitch(sequence, programsByName)
  }

  function stop(at?: number) {
    cancelQueuedSwitch()
    activeController.value?.stop(at)
    activeController.value = null
    activeSequenceKey.value = null
    activePlaybackItem.value = null
    if (rafHandle) cancelAnimationFrame(rafHandle)
    rafHandle = null
  }

  /** Focus a sequence for viewing (Trk Mixer / context screen) without playing it. */
  function focus(sequence: any) {
    focusedSequence.value = sequence
    selectedTrackNumber.value = null
  }

  function selectTrack(trackNumber: number | null) {
    selectedTrackNumber.value = selectedTrackNumber.value === trackNumber ? null : trackNumber
  }

  /**
   * Push the overrides store's current level/pan/mute for one track onto
   * its live bus, if that track's sequence is the one currently playing —
   * makes mixer edits apply instantly instead of waiting for the next
   * loop. No-op if nothing's playing or the track has no bus yet (not
   * used by any scheduled note/audio-layer).
   */
  function syncTrackBus(sequenceKey: number, trackNumber: number, track: any) {
    if (activeSequenceKey.value !== sequenceKey && activeController.value?.currentSequenceKey !== sequenceKey) return
    const bus = activeController.value?.trackBuses?.get(trackNumber)
    if (!bus) return
    const overrides = useOverridesStore()
    bus.setLevel(overrides.getTrackLevel(sequenceKey, track))
    bus.setPan(overrides.getTrackPan(sequenceKey, track))
    bus.setMuted(!overrides.isTrackAudible(sequenceKey, trackNumber, track))
    bus.setSend(overrides.fxEnabled ? overrides.getTrackSend(sequenceKey, track) : 0)
  }

  /** Same, but for every track in a sequence — used after a solo toggle, which can change everyone's audibility at once. */
  function syncAllTrackBuses(sequenceKey: number, tracks: any[]) {
    if (activeSequenceKey.value !== sequenceKey && activeController.value?.currentSequenceKey !== sequenceKey) return
    for (const track of tracks) syncTrackBus(sequenceKey, track.number, track)
  }

  function flashPad(event: {
    programName: string
    padIndex: number
    trackNumber?: number
    note: number
    velocity?: number
    noteFallback?: any
  }) {
    lastFlash.value = {
      programName: event.programName,
      padIndex: event.padIndex,
      trackNumber: event.trackNumber,
      note: event.note,
      velocity: event.velocity ?? 110,
      noteFallback: event.noteFallback ?? null,
      seq: flashSeq++,
    }
  }

  return {
    activeSequenceKey,
    activeController,
    activePlaybackItem,
    queuedSequence,
    focusedSequence,
    selectedTrackNumber,
    lastFlash,
    playheadBar,
    isSong,
    expandedSongEntries,
    play,
    restartIfPlaying,
    toggle,
    stop,
    focus,
    selectTrack,
    syncTrackBus,
    syncAllTrackBuses,
    flashPad,
  }
})
